import { runPullRequestCommand } from "../pullRequestCommand";
import type {
  PullRequestDetail,
  PullRequestDiscussionInput,
  PullRequestEditResult,
} from "@afar-research/contracts";
import { useStore } from "zustand";
import type { PullRequestSurfaceDescriptor } from "../pullRequestSurfaceStore";
import type { PullRequestDiscussionSession } from "../pullRequestDiscussion";
import { invalidatePullRequest } from "../pullRequestInvalidation";
import { newCommandId } from "../lib/utils";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

export function DiscussionForm({
  execute,
  session,
  descriptor,
  detail,
  enabled,
}: {
  readonly execute: (input: PullRequestDiscussionInput) => Promise<PullRequestEditResult>;
  readonly session: PullRequestDiscussionSession;
  readonly descriptor: PullRequestSurfaceDescriptor;
  readonly detail: PullRequestDetail;
  readonly enabled: boolean;
}) {
  const { action, command, pending, result } = useStore(session);
  const canSubmit = enabled && detail.viewerPermissions.comment && detail.capabilities.comment;
  const unresolved = command !== null && result?.status !== "settled";
  const run = (input: PullRequestDiscussionInput) =>
    runPullRequestCommand({
      session,
      input,
      enabled,
      execute,
      onAccepted: () => {
        session.setState({ action: { kind: "comment", body: "" } });
        invalidatePullRequest({
          ...input,
          partitions:
            input.action.kind === "reaction"
              ? ["summary", "timeline"]
              : ["summary", "timeline", "list"],
        });
      },
    });
  return (
    // Following T3Code's comment composer: a remark is a body and a button. Reactions live on
    // the remark they belong to, and editing lives on the remark being edited.
    <section aria-label="Pull Request discussion" className="grid gap-3">
      {result && (
        <p className="text-xs text-muted-foreground" role="status">
          {result.status === "settled"
            ? result.receipt.status === "accepted"
              ? "Discussion updated."
              : result.receipt.result.message
            : result.message}
        </p>
      )}
      {pending ? (
        <p className="text-xs text-muted-foreground" role="status">
          Updating discussion…
        </p>
      ) : unresolved ? (
        <Button disabled={!enabled} onClick={() => void run(command)} size="xs" variant="outline">
          Check outcome
        </Button>
      ) : (
        <form
          className="grid gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (!detail.projectRevision || !canSubmit) return;
            void run({
              environmentId: descriptor.environmentId,
              projectId: detail.projectId,
              repository: detail.repository,
              number: detail.number,
              expectedRevision: detail.projectRevision,
              resourceVersion: detail.resourceVersion,
              commandId: newCommandId(),
              action,
            });
          }}
        >
          <Textarea
            aria-label="Comment"
            maxLength={65000}
            onChange={(event) =>
              session.setState({ action: { kind: "comment", body: event.target.value } })
            }
            placeholder="Leave a comment"
            required
            size="sm"
            value={action.kind === "comment" ? action.body : ""}
          />
          <Button
            className="justify-self-end"
            disabled={
              !canSubmit ||
              !detail.projectRevision ||
              action.kind !== "comment" ||
              !action.body.trim()
            }
            size="xs"
            type="submit"
          >
            Comment
          </Button>
        </form>
      )}
    </section>
  );
}
